import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 15-320100 (Analysis of Profit or Loss — By Nature).
 *
 * Stub validator — hard-codes `hasErrors: false` and `hasSuggestedChanges:
 * false`. The role15-320100 sheet is NOT part of the current release, so it
 * stays aligned with its sibling AS-986 sheets (role10/11/14/16/17), whose
 * validators also hard-code `hasSuggestedChanges: false`. No calc-diff is
 * performed here.
 *
 * Before this validator existed, `analysisOfProfitOrLossByNature` was the one
 * `ALL_SHEET_KEYS` entry with no registered validator, so the orchestrator's
 * missing-validator fallback marked the sheet `{ hasErrors: true }`. This
 * stub closes that gap.
 *
 * Parity basis for `hasErrors: false`:
 *
 *   The role15-320100 component publishes its sheet status via exactly one
 *   site —
 *     this.stateService.markSheetInitializedWithStatus(
 *       'analysisOfProfitOrLossByNature', false
 *     );
 *   — i.e. the editor publishes `hasErrors: false` unconditionally for this
 *   sheet (identical to the role 14 sibling). Returning `false` here is
 *   therefore parity-correct, not an arbitrary default.
 *
 * Backlog: when role15-320100 is released, upgrade this stub to call
 * `CalculationService.calculateTotals_320100` + `hasCalculatedFieldMismatch()`
 * for live silent suggested-change detection (re-tier 1 -> 4 at that point —
 * see the role 14 validator for the wired pattern).
 */
export const role15AnalysisOfProfitOrLossByNatureValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.analysisOfProfitOrLossByNature,
  // Tier 1: stub only — no required-field checks, no cross-sheet reads, no
  // calculations. Re-tier to 4 when the calc-diff path is wired (see header).
  tier: 1,
  validate(
    _sheetData: Record<string, any>,
    _context: SheetValidationContext
  ): PureSheetValidationResult {
    return {
      hasErrors: false,
      // Developer Note (TODO): stub — `hasSuggestedChanges` is hard-coded false,
      // so this sheet's dropdown "*" can never light. When released as a live
      // monetary sheet, mirror role13: (1) recompute via
      // CalculationService.calculateTotals_<id> + hasCalculatedFieldMismatch(
      // context.extractedBaseline ?? sheetData, recomputed); (2) add its sheet key
      // to PURE_VERDICT_SHEETS; (3) call evaluateSheetStatus() in the component's
      // runAllValidations(); (4) add its sheet key to CALC_DIFF_SHEETS in
      // suggestion-capability.ts, or the "(*) Optional: Suggestions to refine
      // data" legend will stay hidden on this sheet even once its "*" can light.
      hasSuggestedChanges: false,
      errors: {},
    };
  },
};
